/**
 * GHL survey answers captured from sticky-contact postMessage and stored
 * on Client Prospect for the sales workspace. Live funnel survey is
 * LqlEDJaFbS6i17cfJHfy; older C8UQSwlwEvmsX6PpPBxj keys stay labeled.
 */

export type GhlSurveyAnswer = {
  question: string;
  answer: string;
};

export type GhlSurveySnapshot = {
  answers: GhlSurveyAnswer[];
  submittedAt?: string | null;
};

const MAX_ANSWERS = 16;
const MAX_QUESTION_CHARS = 160;
const MAX_ANSWER_CHARS = 400;

const SYSTEM_KEYS = new Set([
  'customer_id',
  'locationid',
  'location_id',
  'id',
  'uuid',
  'fingerprint',
  'companyid',
  'company_id',
  'form_id',
  'formid',
  'surveyid',
  'survey_id',
  'sessionid',
  'session_id',
]);

type FieldMap = {
  question: string;
  keys: readonly string[];
};

/** Live survey field ids / query keys → sales labels (order = survey slides). */
export const GHL_SURVEY_FIELD_MAP: readonly FieldMap[] = [
  {
    question: 'Posture classification',
    keys: [
      'posture_classification',
      'contact.posture_classification',
      'posture-classification',
      'single_dropdown_12t75',
      'contact.single_dropdown_12t75',
    ],
  },
  { question: 'First and Last Name', keys: ['full_name', 'contact.full_name', 'name'] },
  { question: 'Phone Number', keys: ['phone', 'contact.phone'] },
  { question: 'Email', keys: ['email', 'contact.email', 'Email'] },
  {
    question: "What's your biggest concern with your posture right now?",
    keys: [
      'posture_primary_focus',
      'contact.posture_primary_focus',
      'j0dVrz71GA7MQDPlNvNo',
    ],
  },
  {
    question: 'Are you currently dealing with any pain or physical limitations?',
    keys: ['pain_frequency', 'contact.pain_frequency', 'AJ70JedN0mvnADc9ZxJs'],
  },
  {
    question: 'How long have you been dealing with this?',
    keys: ['issue_duration', 'contact.issue_duration', '4kmIttaFFx4ZZjrck89n'],
  },
  {
    question: 'If you could improve one thing about how your body feels or moves, what would it be?',
    keys: [
      'primary_movement_goal',
      'contact.primary_movement_goal',
      'PA0s7JMAvAsR4NoTQd9J',
    ],
  },
  {
    question: 'How do you prefer to address your biomechanical findings?',
    keys: [
      'preferred_solution_tier',
      'contact.preferred_solution_tier',
      'vdNNjRRJTeSyAwLu6TqI',
    ],
  },
  {
    question: 'Country',
    keys: ['single_dropdown_12v3p', 'contact.single_dropdown_12v3p', 'jLkGEQYVdqKFneK5rNXl'],
  },
  {
    question: 'Age',
    keys: ['Ezs5bJMzyAuBGZevNG7m', 'single_dropdown_164gc', 'contact.age', 'age'],
  },
  {
    question: 'Yearly income',
    keys: [
      'XrzKujMIvdMKeYOTFC8P',
      'single_dropdown_12j9m_13l8k',
      'contact.single_dropdown_12j9m_13l8k',
      'contact.XrzKujMIvdMKeYOTFC8P',
    ],
  },
  {
    question: 'Main goal',
    keys: [
      'single_dropdown_12rpj_12qe9_13emr_123pk',
      'contact.single_dropdown_12rpj_12qe9_13emr_123pk',
      'pTcDwqk3OvDe8lEoEbRz',
    ],
  },
  {
    question: 'How urgently do you want to fix your posture/pain?',
    keys: [
      'GiIb9moYP63ETQDlOxIM',
      'contact.how_urgently_do_you_want_to_fix_your_posturepain',
      'how_urgently_do_you_want_to_fix_your_posturepain',
    ],
  },
];

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function unwrapStickyContact(data: unknown): Record<string, unknown> | null {
  if (isPlainObject(data)) {
    if ('customer_id' in data || Object.keys(data).length > 0) {
      return data;
    }
    return null;
  }
  if (!Array.isArray(data)) return null;
  for (const entry of data) {
    if (typeof entry !== 'string' || !entry.includes('customer_id')) continue;
    try {
      const parsed = JSON.parse(entry) as unknown;
      if (isPlainObject(parsed)) return parsed;
    } catch {
      /* ignore malformed sticky blob */
    }
  }
  return null;
}

function clip(value: string, max: number): string {
  const trimmed = value.trim();
  if (trimmed.length <= max) return trimmed;
  return trimmed.slice(0, max);
}

export function formatSurveyAnswerValue(value: unknown): string | null {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (Array.isArray(value)) {
    const parts = value
      .map((entry) => formatSurveyAnswerValue(entry))
      .filter((entry): entry is string => Boolean(entry));
    return parts.length ? parts.join(', ') : null;
  }
  return null;
}

function isSystemKey(key: string): boolean {
  return SYSTEM_KEYS.has(key.toLowerCase());
}

function humanizeKey(key: string): string {
  const leaf = key.includes('.') ? key.slice(key.lastIndexOf('.') + 1) : key;
  return leaf.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
}

function lookupMappedAnswer(
  sticky: Record<string, unknown>,
  keys: readonly string[],
): string | null {
  for (const key of keys) {
    if (!Object.prototype.hasOwnProperty.call(sticky, key)) continue;
    const formatted = formatSurveyAnswerValue(sticky[key]);
    if (formatted) return formatted;
  }
  return null;
}

/**
 * Read labeled survey answers from a GHL sticky-contact postMessage or object.
 * Known fields first (survey order); leftover non-system strings follow.
 */
export function extractGhlSurveySnapshot(data: unknown): GhlSurveySnapshot | null {
  const sticky = unwrapStickyContact(data);
  if (!sticky) return null;

  const answers: GhlSurveyAnswer[] = [];
  const consumed = new Set<string>();

  for (const field of GHL_SURVEY_FIELD_MAP) {
    const answer = lookupMappedAnswer(sticky, field.keys);
    if (!answer) continue;
    answers.push({ question: field.question, answer });
    for (const key of field.keys) {
      if (Object.prototype.hasOwnProperty.call(sticky, key)) consumed.add(key);
    }
  }

  for (const [key, value] of Object.entries(sticky)) {
    if (consumed.has(key) || isSystemKey(key)) continue;
    const answer = formatSurveyAnswerValue(value);
    if (!answer) continue;
    const question = humanizeKey(key);
    if (!question) continue;
    answers.push({ question, answer });
  }

  if (answers.length === 0) return null;
  return { answers };
}

export function sanitizeGhlSurveySnapshot(input: unknown): GhlSurveySnapshot | null {
  if (!isPlainObject(input) || !Array.isArray(input.answers)) return null;
  const answers: GhlSurveyAnswer[] = [];
  for (const raw of input.answers.slice(0, MAX_ANSWERS)) {
    if (!isPlainObject(raw)) continue;
    if (typeof raw.question !== 'string' || typeof raw.answer !== 'string') continue;
    const question = clip(raw.question, MAX_QUESTION_CHARS);
    const answer = clip(raw.answer, MAX_ANSWER_CHARS);
    if (!question || !answer) continue;
    answers.push({ question, answer });
  }
  if (answers.length === 0) return null;

  let submittedAt: string | null | undefined;
  if (typeof input.submittedAt === 'string') {
    const parsed = new Date(input.submittedAt);
    submittedAt = Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
  }

  return submittedAt === undefined ? { answers } : { answers, submittedAt };
}

export function mapGhlSurveyFromDoc(data: unknown): GhlSurveySnapshot | undefined {
  return sanitizeGhlSurveySnapshot(data) ?? undefined;
}
